import React, { useState, useEffect } from 'react';
import './InstallPrompt.css';

function InstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const DISMISS_KEY = 'install-prompt-never-show';

  useEffect(() => {
    // Check if user has permanently dismissed this prompt
    const isDismissedPermanently = localStorage.getItem(DISMISS_KEY);
    if (isDismissedPermanently) {
      return;
    }

    // Check if already installed
    const isInstalled = window.navigator.standalone === true ||
                       window.matchMedia('(display-mode: standalone)').matches;
    if (isInstalled) {
      return;
    }

    // Detect if user has visited before (show prompt on 2nd visit)
    const visitCount = parseInt(localStorage.getItem('visitCount') || '0', 10);
    localStorage.setItem('visitCount', String(visitCount + 1));

    // Detect platform
    const ua = navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(ua) && !window.MSStream;
    const isAndroidDevice = /android/.test(ua);

    setIsIos(isIosDevice);
    setIsAndroid(isAndroidDevice);

    // Show prompt on second visit (Android only - iOS users see manual instructions when needed)
    if (visitCount >= 1 && isAndroidDevice) {
      setTimeout(() => setShowPrompt(true), 1500);
    }

    // Listen for beforeinstallprompt event (Android)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (visitCount >= 1) {
        setShowPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
  };

  const handleNeverShowAgain = () => {
    localStorage.setItem(DISMISS_KEY, 'true');
    setShowPrompt(false);
  };

  // Only show popup for Android. iOS users should use manual Safari > Share > Add to Home Screen
  if (!showPrompt || (!isAndroid && !deferredPrompt)) {
    return null;
  }

  return (
    <div className="install-prompt-overlay" onClick={handleDismiss}>
      <div className="install-prompt-modal" onClick={(e) => e.stopPropagation()}>
        <div className="install-prompt-header">
          <button
            className="install-prompt-close"
            onClick={handleDismiss}
            title="Close"
          >
            ✕
          </button>
          <h2 className="install-prompt-title">📱 Install CoachPad</h2>
          <p className="install-prompt-subtitle">Get instant access on your home screen</p>
        </div>

        <div className="install-prompt-benefits">
          <div className="benefit-item">
            <span className="benefit-icon">⚡</span>
            <span className="benefit-text">Quick access from home screen</span>
          </div>
          <div className="benefit-item">
            <span className="benefit-icon">🔋</span>
            <span className="benefit-text">Works offline with cached data</span>
          </div>
          <div className="benefit-item">
            <span className="benefit-icon">🎯</span>
            <span className="benefit-text">Full-screen app experience</span>
          </div>
        </div>

        <div className="install-prompt-instructions">
          {isAndroid && deferredPrompt && (
            <div className="instructions-section android-section">
              <h3 className="instructions-title">🤖 Android Installation</h3>
              <p className="instructions-text">
                Ready to install? Just tap the button below!
              </p>
              <button
                className="install-btn install-btn-primary"
                onClick={handleInstallClick}
              >
                📥 Install App
              </button>
            </div>
          )}

          {isIos && (
            <div className="instructions-section ios-section">
              <h3 className="instructions-title">🍎 iOS Installation</h3>

              {/* App-Like Experience Benefits */}
              <div className="ios-app-benefits">
                <p className="ios-benefits-title">✨ Get the App Experience:</p>
                <div className="ios-benefit-box">
                  <span className="ios-benefit-icon">📲</span>
                  <span className="ios-benefit-text">App icon on home screen</span>
                </div>
                <div className="ios-benefit-box">
                  <span className="ios-benefit-icon">🖥️</span>
                  <span className="ios-benefit-text">Full-screen app interface (no browser)</span>
                </div>
                <div className="ios-benefit-box">
                  <span className="ios-benefit-icon">⚡</span>
                  <span className="ios-benefit-text">Lightning fast launch from home screen</span>
                </div>
              </div>

              {/* Visual Step-by-Step Walkthrough */}
              <div className="ios-walkthrough">
                {/* Step 1 */}
                <div className="ios-step">
                  <div className="ios-step-header">
                    <span className="ios-step-number">1</span>
                    <span className="ios-step-title">Tap Share Button</span>
                  </div>
                  <p className="ios-step-description">
                    Look for the <strong>Share</strong> icon (arrow pointing up from a square) in Safari's bottom toolbar
                  </p>
                  <div className="ios-step-visual">
                    <div className="safari-bar">
                      <span className="safari-element">⬅️</span>
                      <span className="safari-element">➡️</span>
                      <span className="safari-element safari-highlight">⬆️</span>
                    </div>
                    <p className="visual-label">Safari bottom toolbar</p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="ios-step">
                  <div className="ios-step-header">
                    <span className="ios-step-number">2</span>
                    <span className="ios-step-title">Scroll & Select "Add to Home Screen"</span>
                  </div>
                  <p className="ios-step-description">
                    A menu will appear. You may need to scroll down to find <strong>"Add to Home Screen"</strong>
                  </p>
                  <div className="ios-step-visual">
                    <div className="ios-menu">
                      <div className="menu-item">Copy</div>
                      <div className="menu-item">Open in New Tab</div>
                      <div className="menu-item menu-scroll">↓ Scroll ↓</div>
                      <div className="menu-item menu-highlight">Add to Home Screen</div>
                    </div>
                    <p className="visual-label">Share menu</p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="ios-step">
                  <div className="ios-step-header">
                    <span className="ios-step-number">3</span>
                    <span className="ios-step-title">Name & Add</span>
                  </div>
                  <p className="ios-step-description">
                    A popup will show "CoachPad" as the name. Simply tap <strong>"Add"</strong> in the top right corner
                  </p>
                  <div className="ios-step-visual">
                    <div className="ios-dialog">
                      <div className="dialog-header">
                        <span className="dialog-title">CoachPad</span>
                        <span className="dialog-button dialog-highlight">Add</span>
                      </div>
                      <div className="dialog-content">
                        <div className="icon-preview">🎯</div>
                        <p>App will be saved</p>
                      </div>
                    </div>
                    <p className="visual-label">Confirmation dialog</p>
                  </div>
                </div>

                {/* Done */}
                <div className="ios-step ios-step-done">
                  <div className="ios-step-header">
                    <span className="ios-step-number">✓</span>
                    <span className="ios-step-title">Done! 🎉</span>
                  </div>
                  <p className="ios-step-description">
                    CoachPad now appears on your home screen as a real app. Tap to open instantly with no browser UI!
                  </p>
                </div>
              </div>
            </div>
          )}

          {isAndroid && !deferredPrompt && (
            <div className="instructions-section android-section">
              <h3 className="instructions-title">🤖 Android Installation</h3>
              <ol className="instructions-steps">
                <li>
                  Tap the <strong>three-dot menu</strong> (⋮) in Chrome's top right
                </li>
                <li>
                  Tap <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong>
                </li>
                <li>
                  Confirm by tapping <strong>"Install"</strong>
                </li>
                <li>
                  CoachPad will now appear on your home screen! 🎉
                </li>
              </ol>
            </div>
          )}
        </div>

        <div className="install-prompt-actions">
          <button
            className="install-btn install-btn-secondary"
            onClick={handleDismiss}
          >
            Maybe Later
          </button>
          {isAndroid && deferredPrompt && (
            <button
              className="install-btn install-btn-primary"
              onClick={handleInstallClick}
            >
              Install Now
            </button>
          )}
        </div>

        <div className="install-prompt-footer">
          <small>💡 Tip: Once installed, you can use CoachPad even without internet connection!</small>
          <button
            className="install-prompt-never-show"
            onClick={handleNeverShowAgain}
            title="Don't show this prompt again"
          >
            Never show this again
          </button>
        </div>
      </div>
    </div>
  );
}

export default InstallPrompt;
